import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  hasModuleAccess,
  hasPermission,
  hasAnyPermission,
  hasTournamentAccess,
  User,
} from '../utils/roleBasedAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredModule?: string;
  /** Single permission required to access the route */
  requiredPermission?: string;
  /** Alternative: require any of these permissions to access the route */
  anyOfPermissions?: string[];
  /** Restrict a direct tournament URL to the user's assigned tournament scope. */
  requireTournamentAccess?: boolean;
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredModule, 
  requiredPermission,
  anyOfPermissions,
  requireTournamentAccess = false,
  fallbackPath = '/' 
}) => {
  const location = useLocation();
  const params = useParams<{ tournamentId?: string; id?: string }>();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuthentication = async () => {
      // First immediate check
      let hasUser = user && Object.keys(user).length > 0;
      
      if (!hasUser) {
        // If no user, wait a bit for potential Redux state hydration
        // This handles the timing issue during page refresh
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Check again after the delay
        hasUser = user && Object.keys(user).length > 0;
      }
      
      setIsAuthenticated(hasUser);
      setIsChecking(false);
    };

    checkAuthentication();
  }, [user]);

  // Show loading state while checking
  if (isChecking) {
    return <div>Loading...</div>;
  }

  // If no user is logged in, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Create user data object for role and permission checking
  const userData: User = {
    ...user,
    subRole: role,
    permissions: permissions,
  };

  // Avoid denying access while permissions are still loading after hydration.
  // We consider permissions "loaded" once permissionCodes exists (even if empty).
  const rawPermissions: any = permissions;
  const permissionsLoaded =
    rawPermissions && Array.isArray(rawPermissions.permissionCodes);

  const needsPermissionsCheck =
    !!requiredModule ||
    !!requiredPermission ||
    !!(anyOfPermissions && anyOfPermissions.length > 0);

  if (needsPermissionsCheck && !permissionsLoaded) {
    return <div>Loading...</div>;
  }

  // Check module-level access if required
  let hasAccess = true;

  if (requiredModule && !hasModuleAccess(userData, requiredModule)) {
    hasAccess = false;
  }

  // Check permission-level access if required
  if (hasAccess && (requiredPermission || (anyOfPermissions && anyOfPermissions.length > 0))) {
    if (requiredPermission) {
      hasAccess = hasPermission(userData, requiredPermission);
    } else if (anyOfPermissions && anyOfPermissions.length > 0) {
      hasAccess = hasAnyPermission(userData, anyOfPermissions);
    }
  }

  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  const tournamentId = params.tournamentId || params.id;
  if (requireTournamentAccess && tournamentId && !hasTournamentAccess(userData, tournamentId)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute; 